<script lang="ts" setup>
import { computed } from 'vue';
import { NOTIFICATION_TYPE } from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import Avatar from '@/components/common/atoms/avatar.vue';
import CommentReaction from '@/components/common/comment/comment-reaction.vue';
import RelativeTime from '@/components/common/molecules/relative-time.vue';
import { NotificationItem } from '@/types';

interface Props {
  type: NotificationItem['type'];
  url?: string;
  isUnread: boolean;
  createdAt: string;
  userId?: number | null;
  imageUrl?: string;
  contentTitle?: string | null;
  preview?: string | null;
  reactions?: Record<string, number>;
  groupName?: string | null;
}

const props = defineProps<Props>();

const contentTitleClass = computed(() =>
  props.isUnread ? 'c-title-bold--m' : 'c-text-regular--m',
);
</script>

<template>
  <component
    :is="url ? 'router-link' : 'div'"
    class="m-notification-card"
    :to="url"
    :class="{ 'no-link': url === '' }"
  >
    <div
      class="check-mark"
      v-if="
        type === NOTIFICATION_TYPE.FIRST_PERSONAL_NEWS ||
        type === NOTIFICATION_TYPE.SURVEY_HISTORY ||
        type === NOTIFICATION_TYPE.PATENT_FTO_ANALYSIS_COMPLETION
      "
    >
      <DgrIcon
        size="large"
        name="check-circle-fill"
        :keep-fill="false"
        class="primary"
      />
    </div>

    <div
      class="check-mark"
      v-else-if="type === NOTIFICATION_TYPE.UPDATE_THEME_FEED"
    >
      <DgrIcon size="large" :keep-fill="false" name="sync" class="sync" />
    </div>

    <Avatar v-else :image-url="imageUrl"></Avatar>

    <div class="notification-content">
      <div class="notification-text c-text-regular--m">
        <div class="notification-message">
          <div :class="contentTitleClass">
            <slot />
          </div>
          <span v-if="isUnread" class="unread-badge c-title-bold--xs"
            >未読</span
          >
        </div>
        <div class="notification-time c-text-regular--xs">
          <RelativeTime :datetime="createdAt" :hide-tooltip="true" />
        </div>
      </div>

      <div
        v-if="contentTitle"
        class="notification-content-title"
        :class="contentTitleClass"
      >
        {{ contentTitle }}
      </div>

      <div v-if="preview" class="notification-preview">
        <div class="notification-preview-inner c-text-regular--s">
          {{ preview }}
        </div>
      </div>

      <div v-if="reactions" class="notification-reactions">
        <CommentReaction
          v-for="(count, reaction) in reactions"
          :reaction="reaction"
          :count="count"
          :read-only="true"
          :key="reaction"
        />
      </div>

      <div v-if="groupName" class="notification-group-name c-text-regular--s">
        グループ: {{ groupName }}
      </div>
    </div>
  </component>
</template>
<style lang="scss" scoped>
.m-notification-card {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  width: 592px;
  max-width: 100%;
  box-sizing: border-box;
  padding: 16px;
  background-color: white;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  &:hover {
    border: 1px solid $color-gray600;
  }
  .notification-content {
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .notification-text {
    color: $color-gray1000;
  }
  &:deep(a) {
    &:hover {
      text-decoration: underline;
    }
  }
  &:deep(.unread a) {
    color: $color-gray1000;
    &:hover {
      text-decoration: underline;
    }
  }
  .notification-time {
    color: $color-gray600;
  }
  .primary {
    color: $color-green600;
  }
  .sync {
    color: $color-gray400;
  }
  .avatar {
    width: 48px;
    height: 48px;

    :deep(.avatar-size--l) {
      width: 46px;
      height: 46px;
    }
  }
  .notification-message {
    display: flex;
    align-items: flex-start;
    gap: 4px;
  }
  .unread-badge {
    color: $color-orange1000;
    border: 1px solid $color-orange1000;
    padding: 2px 8px;
    border-radius: 20px;
    margin-left: auto;
    flex: 0 0 auto;
  }
  .notification-content-title {
    color: $color-gray1000;
    overflow: hidden;
    display: -webkit-box;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
  .notification-preview {
    border: 1px solid $color-gray400;
    border-radius: 4px;
    padding: 8px;

    // -webkit-line-clampによるtruncateがある要素にpaddingがあると
    // truncateしたはずのテキストが見切れることがあるのでinnerに分離する
    .notification-preview-inner {
      color: $color-gray1000;
      overflow: hidden;
      display: -webkit-box;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }
  }
  .notification-reactions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .notification-group-name {
    color: $color-gray1000;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
:deep(.no-link) {
  cursor: auto;
}
</style>
